import { observable, action, set, runInAction, computed } from 'mobx';
import { dispatch } from 'rfx-core';
import _ from 'lodash';

import { getStore } from '#/shared/getStores';

import BaseUploader, { KnownFieldConfig } from './BaseUploader';

type NoShowRemediationRow = { partnerId: string; shiftId: string };

type RowVerdict = NoShowRemediationRow & { detail?: string; verdict: string };

export default class NoShowRemediationUploader extends BaseUploader {
  constructor() {
    super({ title: 'ui.noShowRemediationUploader' });

    set(this.saveResults, {
      dupe: [],
      incomplete: [],
      invalid: [],
      new: [],
    });

    return this;
  }

  @observable
  list = [];

  csvData = [
    ['shiftId', 'partnerId'],
    [
      '371edb32-06a7-49cf-a382-d38e2c0aa795',
      'b657c1aa-e49c-42af-a0db-07bfb8d80f7e',
    ],
    [
      '3a000b44-0e52-4d62-bb67-631f54f411c3',
      '9a000b44-0e52-4d62-bb67-631f54f411c3',
    ],
  ];

  @observable
  verdicts: RowVerdict[] = [];

  @observable
  hasPreviewed = false;

  @observable
  scheduledChunks = 0;

  @observable
  confirmedRows: NoShowRemediationRow[] = [];

  @observable
  outcomeVerdicts: RowVerdict[] = [];

  @observable
  isCheckingOutcome = false;

  @computed
  get knownFields(): KnownFieldConfig[] {
    return [{ key: 'partnerId' }, { key: 'shiftId' }];
  }

  @computed
  get knownHeaders() {
    return _.map(this.knownFields, 'key');
  }

  @computed
  get verdictCounts() {
    return _.countBy(this.verdicts, 'verdict');
  }

  @computed
  get hasOutcome() {
    return !_.isEmpty(this.outcomeVerdicts);
  }

  // a confirmed row that now reads alreadyRemediated is paid; one still reading
  // ok never took, which is the only honest signal an async job gives us
  @computed
  get remediatedRows() {
    return _.filter(this.outcomeVerdicts, {
      verdict: 'skipped:alreadyRemediated',
    });
  }

  @computed
  get unremediatedRows() {
    return _.reject(this.outcomeVerdicts, {
      verdict: 'skipped:alreadyRemediated',
    });
  }

  @computed
  get remediableRows() {
    return _.filter(this.verdicts, (row) => _.startsWith(row.verdict, 'ok'));
  }

  @action
  clear() {
    super.clear();

    this.list.clear();
    this.verdicts = [];
    this.hasPreviewed = false;
    this.scheduledChunks = 0;
    this.confirmedRows = [];
    this.outcomeVerdicts = [];
    this.isCheckingOutcome = false;
  }

  @action
  async parse({ rows }) {
    const caseHeaders = _.map(rows[0], _.toLower);
    const indices = _.reduce(
      this.knownHeaders,
      (acc, header) => {
        acc[header] = _.indexOf(caseHeaders, _.toLower(header));
        return acc;
      },
      {},
    );

    this.log.debug('Loaded Table Indices', { indices });

    const validRows = _(rows)
      .map((row, i) => {
        if (!i) return null;

        return _.chain(this.knownHeaders)
          .reduce((acc, colKey) => {
            const colIndex = indices[colKey];

            if (colIndex >= 0) {
              acc[colKey] = _.trim(row[colIndex]);
            }
            return acc;
          }, {})
          .value();
      })
      .reject((rowObj) => _.isEmpty(_.omitBy(rowObj, _.isEmpty)))
      .compact()
      .value();

    this.setStatus('Evaluating');

    runInAction(() => {
      this.list.replace(validRows);
      this.verdicts = [];
      this.hasPreviewed = false;
    });

    this.setStatus('Parsed all Rows');
  }

  @action
  async preview() {
    this.setStatus('previewing');
    this.setSaving(true);

    try {
      const { verdicts } = (await getStore('noShowRemediationUploader').create({
        data: { dryRun: true, rows: _.map(this.list, _.clone) },
      })) as unknown as { verdicts: RowVerdict[] };

      runInAction(() => {
        this.verdicts = verdicts;
        this.hasPreviewed = true;
      });

      this.setStatus('previewed');
    } catch (err) {
      this.log.error('Failed to preview no-show remediation rows', err);
      dispatch('ui.snackBar.error', `Sorry, something went wrong, ${err}`);
      this.setStatus('error');
    }

    this.setSaving(false);
  }

  @action
  async checkOutcome() {
    if (_.isEmpty(this.confirmedRows)) {
      return;
    }

    this.isCheckingOutcome = true;

    try {
      const { verdicts } = (await getStore('noShowRemediationUploader').create({
        data: { dryRun: true, rows: _.map(this.confirmedRows, _.clone) },
      })) as unknown as { verdicts: RowVerdict[] };

      runInAction(() => {
        this.outcomeVerdicts = verdicts;
      });
    } catch (err) {
      this.log.error('Failed to check no-show remediation outcome', err);
      dispatch('ui.snackBar.error', `Sorry, something went wrong, ${err}`);
    }

    runInAction(() => {
      this.isCheckingOutcome = false;
    });
  }

  @action
  async confirm() {
    if (!this.hasPreviewed || _.isEmpty(this.remediableRows)) {
      return;
    }

    this.setStatus('saving');
    this.setSaving(true);

    dispatch('audit.create', {
      data: {
        action: 'Confirm No-Show Remediation',
        extra: {
          file: _.pick(this.file, ['name', 'type', 'size']),
          rowCount: _.size(this.remediableRows),
        },
      },
    });

    try {
      const rows = _.map(this.remediableRows, (row) =>
        _.pick(row, ['partnerId', 'shiftId']),
      );

      const { scheduledChunks } = (await getStore(
        'noShowRemediationUploader',
      ).create({ data: { dryRun: false, rows } })) as unknown as {
        scheduledChunks: number;
      };

      runInAction(() => {
        this.scheduledChunks = scheduledChunks;
        this.confirmedRows = rows;
        this.outcomeVerdicts = [];
      });

      this.setStatus('saved');
    } catch (err) {
      this.log.error('Failed to confirm no-show remediation rows', err);
      dispatch('ui.snackBar.error', `Sorry, something went wrong, ${err}`);
      this.setStatus('error');
    }

    this.setSaving(false);
  }
}
